// @file Composable that enables an element to be a drop target.
import {
  extractItemData,
  extractLogicalClosestEdge,
  extractRelativePositionToTarget,
  isInnermostDropTarget,
  isItemData,
  makeItemData,
  type CanDropPayload,
  type DragData,
  type DraggableSource,
  type ItemData,
  type LogicalEdge,
  type OnDropPayload,
} from '@@/pragmatic-drag-and-drop/helpers'
import { attachClosestEdge, type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import type {
  DropTargetArgs,
  DropTargetEventPayloadMap,
} from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types'
import {
  dropTargetForElements,
  type ElementDropTargetEventPayloadMap,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import type { CleanupFn, ElementDragType } from '@atlaskit/pragmatic-drag-and-drop/types'
import { mapValues } from 'es-toolkit'
import { computed, isRef, onBeforeUnmount, onMounted, ref, unref, watch, type Ref } from 'vue'

type EventName = keyof DropTargetEventPayloadMap<ElementDragType> | 'canDrop'
type DropTargetListener<T extends EventName> = NonNullable<DropTargetArgs<ElementDragType>[T]>

/**
 * Makes an element a drop target for other draggable elements.
 */
export const useDropTargetElement = <TTargetData extends DragData>({
  elementRef,
  type,
  data,
  ignoresNestedDrops = false,
}: {
  /**
   * Element to be made drop target.
   */
  elementRef: Ref<HTMLElement | undefined>
  /**
   * Used to differentiate multiple types of drop targets on a page.
   */
  type: ItemData['type']
  /**
   * Data to attach with this drop target.
   */
  data: Ref<TTargetData>
  /**
   * Whether to ignore drop events from nested drop targets.
   */
  ignoresNestedDrops?: boolean
}): {
  /**
   * Whether this element is currently being dragged over.
   */
  isDraggingOver: typeof isDraggingOver
  /**
   * Which edge of the drop target is closest to the pointer.
   */
  closestEdgeToPointer: typeof closestEdgeToPointer
  /**
   * Registers a draggable source that can be dropped on this target.
   */
  addDraggableSource: typeof addDraggableSource
} => {
  const draggableSourcesByType = ref<Record<ItemData['type'], DraggableSource>>({})
  const dropTargetListeners = ref<{ [K in EventName]?: Array<DropTargetListener<K>> }>({})

  const allowedEdgesByType = computed(() => {
    return mapValues(
      draggableSourcesByType.value,
      ({ axis }) => (axis === 'horizontal' ? ['left', 'right'] : ['top', 'bottom']) as Edge[],
    )
  })

  const addDropTargetListener = <T extends EventName>(event: T, listener: DropTargetListener<T>): void => {
    dropTargetListeners.value[event] ||= []
    // @ts-expect-error
    dropTargetListeners.value[event]?.push(listener)
  }

  const addDraggableSource = <TSourceData>({
    type,
    axis,
    axisRef,
    canDrop,
    onDragEnter,
    onDrag,
    onDragLeave,
    onDrop,
  }: DraggableSource & {
    /**
     * Whether the draggable element can be dropped on the target.
     */
    canDrop?: (payload: CanDropPayload<TSourceData, TTargetData>) => boolean
    /**
     * When this drop target is entered into.
     */
    onDragEnter?: DropTargetListener<'onDragEnter'>
    /**
     * A throttled update of where the the user is currently dragging.
     */
    onDrag?: DropTargetListener<'onDrag'>
    /**
     * When this drop target is exited from.
     */
    onDragLeave?: DropTargetListener<'onDragLeave'>
    /**
     * The user has finished a drag and drop operation.
     */
    onDrop?: (payload: OnDropPayload<TSourceData, TTargetData>, e: ElementDropTargetEventPayloadMap['onDrop']) => void
  }): void => {
    draggableSourcesByType.value[type] = { type, axis: unref(axisRef) ?? axis }

    if (isRef(axisRef)) {
      watch(axisRef, (axis) => {
        draggableSourcesByType.value = {
          ...draggableSourcesByType.value,
          [type]: { type, axis },
        }
      })
    }

    addDropTargetListener('canDrop', ({ source }) => {
      // Only allow dropping draggable elements of the specified types.
      const sourceType = extractItemData(source)?.type
      if (sourceType !== type) return false
      return (
        canDrop?.({
          sourceItem: extractItemData(source) as ItemData<TSourceData>,
          targetItem: itemData.value as ItemData<TTargetData>,
        }) ?? true
      )
    })

    addDropTargetListener('onDragEnter', (e) => {
      onDragEnter?.(e)
    })

    addDropTargetListener('onDrag', (e) => {
      onDrag?.(e)
    })

    addDropTargetListener('onDragLeave', (e) => {
      onDragLeave?.(e)
    })

    addDropTargetListener('onDrop', (e) => {
      const target = e.location.current.dropTargets[0]

      const sourceItem = extractItemData(e.source) as ItemData<TSourceData>
      // Only handle dropping draggable elements of the specified types.
      if (sourceItem.type !== type) return
      const targetItem = extractItemData(target) as ItemData<TTargetData>

      if (!isItemData(sourceItem) || !isItemData(targetItem)) {
        return
      }

      const relativePositionToTarget = extractRelativePositionToTarget(target)
      if (relativePositionToTarget == null) {
        return
      }

      onDrop?.({ sourceItem, targetItem, relativePositionToTarget }, e)
    })
  }

  const isDraggingOver = ref(false)
  const closestEdgeToPointer = ref<LogicalEdge | null>(null)

  const itemData = computed(() => makeItemData(data.value, type))

  const makeElementDropTarget = (): CleanupFn => {
    if (elementRef.value == null) return () => {}
    return dropTargetForElements({
      element: elementRef.value,
      getData: ({ source, input }) => {
        const sourceType = extractItemData(source)?.type
        // If we can't identify the type, it's not a drag source we should handle.
        // This will eventually be cancelled by the `canDrop` check.
        if (sourceType == null) return {}
        if (elementRef.value == null) return itemData.value
        // Attach the closest edge to the pointer on the drop target.
        return attachClosestEdge(itemData.value, {
          element: elementRef.value,
          input,
          allowedEdges: allowedEdgesByType.value[sourceType],
        })
      },
      getIsSticky: () => true, // Remembers last drop target even if the pointer already leaves it.
      canDrop: (e) => {
        // Allow dropping if any of the draggable sources wants to handle the drop.
        return dropTargetListeners.value.canDrop?.some((listener) => listener(e)) ?? false
      },
      onDragEnter: (e) => {
        dropTargetListeners.value.onDragEnter?.forEach((listener) => listener(e))
      },
      onDrag: (e) => {
        isDraggingOver.value = true
        // If there are nested drop targets all of them will have
        // their `onDrag` callbacks executed.
        // Use `ignoresNestedDrops` to skip handling nested dragging
        // from an outer drop target.
        if (ignoresNestedDrops && !isInnermostDropTarget(e)) {
          closestEdgeToPointer.value = null
          return
        }
        closestEdgeToPointer.value = extractLogicalClosestEdge(e.self)
        dropTargetListeners.value.onDrag?.forEach((listener) => listener(e))
      },
      onDragLeave: (e) => {
        isDraggingOver.value = false
        closestEdgeToPointer.value = null
        dropTargetListeners.value.onDragLeave?.forEach((listener) => listener(e))
      },
      onDrop: (e) => {
        isDraggingOver.value = false

        // If there are nested drop targets all of them will have
        // their `onDrop` callbacks executed.
        // Use `ignoresNestedDrops` to skip handling nested drops
        // from an outer drop target.
        if (ignoresNestedDrops && !isInnermostDropTarget(e)) return
        dropTargetListeners.value.onDrop?.forEach((listener) => listener(e))

        // Reset after callbacks have executed
        closestEdgeToPointer.value = null
      },
    })
  }

  let cleanUp: CleanupFn

  onMounted(() => {
    cleanUp = makeElementDropTarget()
  })

  onBeforeUnmount(() => {
    cleanUp?.()
  })

  return {
    isDraggingOver,
    closestEdgeToPointer,
    addDraggableSource,
  }
}
